import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import {
  getAllInMonthlyCost,
  getPriceCut,
  isRecentlyListed,
} from 'components/marketplace/helpers/certaintyPanel';
import type { PriceCut } from 'components/marketplace/helpers/certaintyPanel';
import { determinePropertyActionability } from 'components/marketplace/helpers/propertyActionability';
import { useCertaintyPanelExperiment } from 'components/marketplace/helpers/useCertaintyPanelExperiment';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';
import { useObservability } from 'helpers/observability';

import type { BuyBoxCertaintyStats } from './BuyBoxStats';

export type CertaintyPanelProperty = Pick<
  MarketplaceProperty,
  | 'addressToken'
  | 'annualPropertyTax'
  | 'buyDirectPrice'
  | 'buyDirectSavings'
  | 'canSelfTour'
  | 'daysOnMarket'
  | 'displayBadge'
  | 'estimatedMonthlyPayment'
  | 'hasCurrentAvailability'
  | 'hoaMonthlyDues'
  | 'listingHistory'
  | 'listingState'
  | 'buyDirectStatus'
  | 'shouldShowGuarantee'
  | 'state'
>;

// Mortgage merchandising is only compliant in Colorado today; the gate keys on
// the listing's state, not the viewer's location.
const MORTGAGE_CTA_STATES: ReadonlySet<string> = new Set(['CO']);

export type CertaintyTreatment = {
  priceCut: PriceCut | null;
  recentlyListed: boolean;
  buyBoxStats: BuyBoxCertaintyStats;
  showGuaranteeCard: boolean;
  showMortgageCta: boolean;
};

const getCertaintyTreatment = (property: CertaintyPanelProperty): CertaintyTreatment | null => {
  const { isActionable, showBuyDirect, disableBuyDirect } =
    determinePropertyActionability(property);
  if (!isActionable) {
    return null;
  }

  const treatment: CertaintyTreatment = {
    priceCut: getPriceCut(property.listingHistory),
    // A page-level pill above the address, so unlike the buy-box stats it does
    // not depend on the Buy Direct card rendering.
    recentlyListed: isRecentlyListed(property.daysOnMarket),
    // BuyBoxStats renders inside the Buy Direct card, which BuyDirectCTA
    // replaces with the tour card when showBuyDirect is false. Left ungated,
    // these stats count as content on listings whose treatment page is
    // identical to control, and those users still record an exposure.
    buyBoxStats: {
      monthlyCostTotal: showBuyDirect ? (getAllInMonthlyCost(property)?.total ?? null) : null,
      showKeysBy: showBuyDirect && !disableBuyDirect,
    },
    showGuaranteeCard: Boolean(property.shouldShowGuarantee),
    showMortgageCta: property.state != null && MORTGAGE_CTA_STATES.has(property.state),
  };

  const hasContent =
    treatment.priceCut != null ||
    treatment.recentlyListed ||
    treatment.buyBoxStats.monthlyCostTotal != null ||
    treatment.buyBoxStats.showKeysBy ||
    treatment.showGuaranteeCard;

  return hasContent ? treatment : null;
};

export type CertaintyPanelSurfaces = {
  // Attach to the element wrapping the treatment-modified region; it stays in
  // layout for both arms so exposure is recorded symmetrically.
  slotRef: RefObject<HTMLDivElement>;
  // Non-null only for genuinely bucketed treatment users on listings where the
  // redesign has something to show; control renders must not read it.
  treatment: CertaintyTreatment | null;
};

export const useCertaintyPanelSurfaces = (
  property: CertaintyPanelProperty,
): CertaintyPanelSurfaces => {
  const slotRef = useRef<HTMLDivElement>(null);
  const { trackJourneyEvent } = useObservability();
  const content = getCertaintyTreatment(property);
  const hasContent = content != null;
  const { identity, variant, isFallback, isExperimentAssignment, isTreatment, trackImpression } =
    useCertaintyPanelExperiment({ enabled: hasContent });
  // Deliberately not `triggerOnce`: that unobserves on first intersection, and
  // the PDP swaps the listing under a mounted PropertySection, so a latched
  // `inView` would report the next listing as seen before it is scrolled to.
  // The exposure key below is what keeps this to one exposure per unit.
  const { inView } = useInViewAfterLoad({ ref: slotRef, threshold: 0 });
  // One exposure per (identity, listing), for every unit seen this session.
  // The listing changes without a remount and a mid-page sign-in re-buckets, so
  // both are new units that owe an exposure; a set rather than a single slot so
  // navigating back to an already-counted listing does not count it twice.
  const impressedKeys = useRef(new Set<string>());

  const addressToken = property.addressToken;
  const exposureKey = `${identity}|${addressToken}`;

  useEffect(() => {
    // Only experiment-backed assignments enter the exposure streams; fallback
    // control and default-served values would count a population the
    // impression stream excludes.
    if (
      inView &&
      hasContent &&
      variant != null &&
      isExperimentAssignment &&
      !impressedKeys.current.has(exposureKey)
    ) {
      impressedKeys.current.add(exposureKey);
      trackImpression();
      trackJourneyEvent('marketplaceProperty', 'certaintyPanelView', {
        addressUuid: addressToken,
        variant,
      });
    }
  }, [
    inView,
    hasContent,
    variant,
    isExperimentAssignment,
    trackImpression,
    trackJourneyEvent,
    addressToken,
    exposureKey,
  ]);

  return {
    slotRef,
    treatment: isTreatment && !isFallback ? content : null,
  };
};
